/**
 * ApparatusView — Top-down SVG vehicle views for the roster.
 *
 * Renders apparatus with personnel shown in their seating positions:
 * - BC Buggy: Ford Explorer Police Interceptor Utility
 * - Engine: Seagrave Marauder II pumper
 * - Tower: Seagrave Aerialscope (longer body, turntable, aerial boom, outriggers)
 * - Rescue: Seagrave heavy rescue (walk-around body, equipment compartments)
 * - Ambulance: F350 with Horton Type C box
 *
 * Each seat shows an avatar (initials placeholder) + last name.
 * Clicking a seat opens the edit modal for officers+.
 */

import type { UnitDef, SeatDef } from '../../../../shared/roster-config';
import { AlertTriangle } from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────

interface Assignment {
  display_name: string;
  member_id: number;
}

interface PendingChange {
  proposed_member_name: string | null;
}

interface ApparatusCardProps {
  unit: UnitDef;
  battalionId: number;
  battalionColor: string;
  getAssignment: (bn: number, unit: string, seat: string) => Assignment | undefined;
  getPendingChange: (bn: number, unit: string, seat: string) => PendingChange | undefined;
  canEdit: boolean;
  onEditSeat: (unitId: string, seatId: string) => void;
  memberPhotos?: Map<number, string>;
}

// ── Seat position within SVG ──────────────────────────────────────────

interface SeatPos {
  seatId: string;
  x: number;
  y: number;
  labelBelow?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function getLastName(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts[parts.length - 1];
}

/** Deterministic color from name for avatar background */
function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hues = [165, 200, 240, 25, 75, 300, 140, 180, 330, 50];
  const hue = hues[Math.abs(hash) % hues.length];
  return `oklch(0.55 0.14 ${hue})`;
}

// ── Seat Slot (rendered inside SVG) ───────────────────────────────────

function SeatSlot({
  x,
  y,
  seatDef,
  assignment,
  pending,
  canEdit,
  onClick,
  r = 22,
  photoUrl,
}: {
  x: number;
  y: number;
  seatDef: SeatDef;
  assignment: Assignment | undefined;
  pending: PendingChange | undefined;
  canEdit: boolean;
  onClick: () => void;
  r?: number;
  photoUrl?: string;
}) {
  const nameY = y + r + 14;
  const clipId = `clip-${x}-${y}`;

  return (
    <g
      className={canEdit ? 'cursor-pointer' : ''}
      onClick={canEdit ? onClick : undefined}
      role={canEdit ? 'button' : undefined}
      tabIndex={canEdit ? 0 : undefined}
      onKeyDown={
        canEdit
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') onClick();
            }
          : undefined
      }
    >
      {/* Hover target (invisible, larger than avatar) */}
      {canEdit && <circle cx={x} cy={y} r={r + 6} fill="transparent" />}

      {assignment ? (
        <>
          {/* Avatar circle (background color, shown if no photo) */}
          <circle cx={x} cy={y} r={r} fill={avatarColor(assignment.display_name)} opacity={0.85} />
          {/* Profile photo (clipped to circle) */}
          {photoUrl && (
            <>
              <defs>
                <clipPath id={clipId}>
                  <circle cx={x} cy={y} r={r} />
                </clipPath>
              </defs>
              <image
                href={photoUrl}
                x={x - r}
                y={y - r}
                width={r * 2}
                height={r * 2}
                clipPath={`url(#${clipId})`}
                preserveAspectRatio="xMidYMid slice"
                style={{ pointerEvents: 'none' }}
              />
            </>
          )}
          {/* Initials (shown if no photo) */}
          {!photoUrl && (
            <text
              x={x}
              y={y + 1}
              textAnchor="middle"
              dominantBaseline="central"
              fill="white"
              fontSize={r * 0.82}
              fontWeight={700}
              fontFamily="var(--font-sans)"
              style={{ pointerEvents: 'none', userSelect: 'none' }}
            >
              {getInitials(assignment.display_name)}
            </text>
          )}
          {/* Last name */}
          <text
            x={x}
            y={nameY}
            textAnchor="middle"
            fill="var(--text-primary)"
            fontSize={11}
            fontWeight={600}
            fontFamily="var(--font-sans)"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            {getLastName(assignment.display_name)}
          </text>
        </>
      ) : (
        <>
          {/* Vacant circle */}
          <circle cx={x} cy={y} r={r} fill="none" stroke="var(--border-muted)" strokeWidth={2} strokeDasharray="5 3" />
          {/* Seat label */}
          <text
            x={x}
            y={y + 1}
            textAnchor="middle"
            dominantBaseline="central"
            fill="var(--text-disabled)"
            fontSize={8}
            fontWeight={600}
            fontFamily="var(--font-sans)"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            {seatDef.label.split(' ')[0].substring(0, 5).toUpperCase()}
          </text>
          {/* "Vacant" label */}
          <text
            x={x}
            y={nameY}
            textAnchor="middle"
            fill="var(--text-disabled)"
            fontSize={9}
            fontStyle="italic"
            fontFamily="var(--font-sans)"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            Vacant
          </text>
        </>
      )}

      {/* Pending indicator ring */}
      {pending && (
        <circle
          cx={x}
          cy={y}
          r={r + 3}
          fill="none"
          stroke="var(--color-warning-400, #FFA726)"
          strokeWidth={2}
          strokeDasharray="4 2"
        />
      )}

      {/* Required seat indicator dot */}
      {seatDef.required && !assignment && (
        <circle cx={x + r - 2} cy={y - r + 2} r={4} fill="var(--color-danger-400, #EF5350)" />
      )}
    </g>
  );
}

// ── Shared SVG component prop type ──────────────────────────────────

interface VehicleSVGProps {
  seats: SeatPos[];
  battalionId: number;
  unitId: string;
  getAssignment: ApparatusCardProps['getAssignment'];
  getPendingChange: ApparatusCardProps['getPendingChange'];
  canEdit: boolean;
  onEditSeat: ApparatusCardProps['onEditSeat'];
  seatDefs: SeatDef[];
  memberPhotos?: Map<number, string>;
}

/** Renders seat positions from the shared seat layout */
function renderSeats(props: VehicleSVGProps, r: number): React.ReactNode {
  const { seats, battalionId, unitId, getAssignment, getPendingChange, canEdit, onEditSeat, seatDefs, memberPhotos } =
    props;
  return seats.map((s) => {
    const def = seatDefs.find((d) => d.id === s.seatId);
    if (!def) return null;
    const a = getAssignment(battalionId, unitId, s.seatId);
    return (
      <SeatSlot
        key={s.seatId}
        x={s.x}
        y={s.y}
        seatDef={def}
        assignment={a}
        pending={getPendingChange(battalionId, unitId, s.seatId)}
        canEdit={canEdit}
        onClick={() => onEditSeat(unitId, s.seatId)}
        r={r}
        photoUrl={a ? memberPhotos?.get(a.member_id) : undefined}
      />
    );
  });
}

// ── Shared Seagrave Marauder II Cab (used by Engine, Tower, Rescue) ──

function SeagraveCab() {
  return (
    <>
      {/* === Cab section (Marauder II) === */}
      <path
        d="M 40 20 Q 40 5 55 5 L 165 5 Q 180 5 180 20
           L 182 95 L 182 130 L 38 130 L 38 95 Z"
        fill="none"
        stroke="var(--border-strong)"
        strokeWidth={2.5}
        strokeLinejoin="round"
      />
      {/* Windshield */}
      <path
        d="M 48 55 L 55 35 L 165 35 L 172 55 Z"
        fill="var(--surface-sunken)"
        stroke="var(--border-default)"
        strokeWidth={1.5}
        opacity={0.5}
      />
      {/* Side mirrors — large rectangular on angled stalks */}
      <line x1={38} y1={48} x2={24} y2={52} stroke="var(--border-default)" strokeWidth={1.5} />
      <rect x={6} y={44} width={18} height={12} rx={2} fill="none" stroke="var(--border-default)" strokeWidth={1.5} />
      <line x1={182} y1={48} x2={196} y2={52} stroke="var(--border-default)" strokeWidth={1.5} />
      <rect x={196} y={44} width={18} height={12} rx={2} fill="none" stroke="var(--border-default)" strokeWidth={1.5} />
      {/* Steering wheel */}
      <circle cx={75} cy={80} r={13} fill="none" stroke="var(--border-default)" strokeWidth={1.5} opacity={0.35} />
      {/* Center console (cab) */}
      <rect
        x={100}
        y={65}
        width={20}
        height={50}
        rx={3}
        fill="var(--surface-sunken)"
        stroke="var(--border-muted)"
        strokeWidth={1}
        opacity={0.4}
      />
      {/* Front bumper / intake */}
      <rect
        x={48}
        y={2}
        width={124}
        height={6}
        rx={2}
        fill="none"
        stroke="var(--border-muted)"
        strokeWidth={1}
        opacity={0.35}
      />
      {/* Front discharge (bumper turret mount) */}
      <circle cx={110} cy={5} r={3} fill="none" stroke="var(--border-muted)" strokeWidth={0.8} opacity={0.3} />
      {/* Light bar (roof of cab) */}
      <rect
        x={50}
        y={58}
        width={120}
        height={5}
        rx={2.5}
        fill="var(--surface-sunken)"
        stroke="var(--border-muted)"
        strokeWidth={0.8}
        opacity={0.45}
      />
      {/* Headlights */}
      <rect x={45} y={5} width={14} height={5} rx={2} fill="var(--color-warning-400, #FFD54F)" opacity={0.3} />
      <rect x={161} y={5} width={14} height={5} rx={2} fill="var(--color-warning-400, #FFD54F)" opacity={0.3} />
      {/* Wheel wells — front (cab) */}
      <path d="M 35 85 Q 28 100 35 115" fill="none" stroke="var(--border-muted)" strokeWidth={1.2} opacity={0.35} />
      <path d="M 185 85 Q 192 100 185 115" fill="none" stroke="var(--border-muted)" strokeWidth={1.2} opacity={0.35} />
    </>
  );
}

/** Shared crew compartment (6-person, used by Engine, Tower, Rescue) */
function SeagraveCrewCompartment() {
  return (
    <>
      {/* === Crew compartment === */}
      <rect
        x={35}
        y={130}
        width={150}
        height={155}
        rx={3}
        fill="none"
        stroke="var(--border-strong)"
        strokeWidth={2.5}
      />
      <line x1={35} y1={130} x2={185} y2={130} stroke="var(--border-strong)" strokeWidth={2.5} />
      {/* Interior partition (between rear-facing and forward-facing) */}
      <line
        x1={45}
        y1={215}
        x2={175}
        y2={215}
        stroke="var(--border-muted)"
        strokeWidth={1}
        strokeDasharray="4 3"
        opacity={0.5}
      />
      {/* Seat backs (rear-facing row — thick arc behind seats, faces toward rear of truck) */}
      <path
        d="M 58 152 A 14 14 0 0 1 86 152"
        fill="none"
        stroke="var(--border-muted)"
        strokeWidth={1.5}
        opacity={0.3}
      />
      <path
        d="M 134 152 A 14 14 0 0 1 162 152"
        fill="none"
        stroke="var(--border-muted)"
        strokeWidth={1.5}
        opacity={0.3}
      />
      {/* Seat backs (forward-facing row — arc below seats, faces toward front of truck) */}
      <path
        d="M 58 258 A 14 14 0 0 0 86 258"
        fill="none"
        stroke="var(--border-muted)"
        strokeWidth={1.5}
        opacity={0.3}
      />
      <path
        d="M 134 258 A 14 14 0 0 0 162 258"
        fill="none"
        stroke="var(--border-muted)"
        strokeWidth={1.5}
        opacity={0.3}
      />
      {/* Cab seat backs (forward-facing, arc behind cab seats) */}
      <path d="M 58 68 A 14 14 0 0 1 86 68" fill="none" stroke="var(--border-muted)" strokeWidth={1.2} opacity={0.25} />
      <path
        d="M 134 68 A 14 14 0 0 1 162 68"
        fill="none"
        stroke="var(--border-muted)"
        strokeWidth={1.2}
        opacity={0.25}
      />
    </>
  );
}

// ── Ford Explorer Police Interceptor Utility (BC Buggy) ─────────────

function ExplorerSVG(props: VehicleSVGProps) {
  return (
    <svg viewBox="0 0 200 310" className="w-full max-w-[200px]" role="img" aria-label="Ford Explorer top-down view">
      {/* Body outline — organic SUV shape with rounded hood, sculpted fenders */}
      <path
        d="M 56 15 Q 100 4 144 15
           Q 160 19 163 35 L 165 65 L 167 90
           Q 168 110 167 130 L 167 200
           Q 168 230 167 250 L 164 270
           Q 160 288 140 290 L 60 290 Q 40 288 36 270
           L 33 250 Q 32 230 33 200 L 33 130
           Q 32 110 33 90 L 35 65 L 37 35
           Q 40 19 56 15 Z"
        fill="none"
        stroke="var(--border-strong)"
        strokeWidth={2.5}
        strokeLinejoin="round"
      />
      {/* Hood sculpt line (center crease) */}
      <path d="M 100 16 L 100 48" stroke="var(--border-muted)" strokeWidth={1} opacity={0.3} />
      {/* Hood edge contours */}
      <path d="M 56 18 Q 58 32 48 50" stroke="var(--border-muted)" strokeWidth={1} opacity={0.25} />
      <path d="M 144 18 Q 142 32 152 50" stroke="var(--border-muted)" strokeWidth={1} opacity={0.25} />
      {/* Windshield (large, A-pillar to roof) */}
      <path
        d="M 43 72 L 52 50 Q 100 44 148 50 L 157 72 Z"
        fill="var(--surface-sunken)"
        stroke="var(--border-default)"
        strokeWidth={1.5}
        opacity={0.5}
      />
      {/* A-pillar lines */}
      <line x1={43} y1={72} x2={40} y2={82} stroke="var(--border-default)" strokeWidth={1.2} opacity={0.35} />
      <line x1={157} y1={72} x2={160} y2={82} stroke="var(--border-default)" strokeWidth={1.2} opacity={0.35} />
      {/* Side windows (front) */}
      <path
        d="M 40 82 L 38 105 L 42 105 L 44 82 Z"
        fill="var(--surface-sunken)"
        stroke="var(--border-default)"
        strokeWidth={1}
        opacity={0.3}
      />
      <path
        d="M 160 82 L 162 105 L 158 105 L 156 82 Z"
        fill="var(--surface-sunken)"
        stroke="var(--border-default)"
        strokeWidth={1}
        opacity={0.3}
      />
      {/* B-pillar */}
      <line x1={42} y1={105} x2={42} y2={115} stroke="var(--border-default)" strokeWidth={1.5} opacity={0.35} />
      <line x1={158} y1={105} x2={158} y2={115} stroke="var(--border-default)" strokeWidth={1.5} opacity={0.35} />
      {/* Side windows (rear) */}
      <path
        d="M 42 115 L 38 165 L 42 165 L 44 115 Z"
        fill="var(--surface-sunken)"
        stroke="var(--border-default)"
        strokeWidth={1}
        opacity={0.25}
      />
      <path
        d="M 158 115 L 162 165 L 158 165 L 156 115 Z"
        fill="var(--surface-sunken)"
        stroke="var(--border-default)"
        strokeWidth={1}
        opacity={0.25}
      />
      {/* C/D-pillar (rear quarter) */}
      <line x1={40} y1={165} x2={42} y2={180} stroke="var(--border-default)" strokeWidth={1.5} opacity={0.35} />
      <line x1={160} y1={165} x2={158} y2={180} stroke="var(--border-default)" strokeWidth={1.5} opacity={0.35} />
      {/* Rear quarter windows (small) */}
      <path
        d="M 42 180 L 40 200 L 44 200 L 45 180 Z"
        fill="var(--surface-sunken)"
        stroke="var(--border-default)"
        strokeWidth={1}
        opacity={0.2}
      />
      <path
        d="M 158 180 L 160 200 L 156 200 L 155 180 Z"
        fill="var(--surface-sunken)"
        stroke="var(--border-default)"
        strokeWidth={1}
        opacity={0.2}
      />
      {/* D-pillar (rear) */}
      <line x1={42} y1={200} x2={44} y2={215} stroke="var(--border-default)" strokeWidth={1.5} opacity={0.35} />
      <line x1={158} y1={200} x2={156} y2={215} stroke="var(--border-default)" strokeWidth={1.5} opacity={0.35} />
      {/* Rear window */}
      <path
        d="M 48 220 Q 100 216 152 220 L 150 245 Q 100 250 50 245 Z"
        fill="var(--surface-sunken)"
        stroke="var(--border-default)"
        strokeWidth={1.5}
        opacity={0.35}
      />
      {/* Sunroof / moonroof (roof panel) */}
      <rect
        x={62}
        y={85}
        width={76}
        height={95}
        rx={8}
        fill="var(--surface-sunken)"
        stroke="var(--border-muted)"
        strokeWidth={1}
        opacity={0.25}
      />
      {/* Side mirrors — compact rectangular, flush-mounted on A-pillar */}
      <line x1={38} y1={72} x2={26} y2={70} stroke="var(--border-default)" strokeWidth={1.5} />
      <rect x={16} y={66} width={11} height={8} rx={2} fill="none" stroke="var(--border-default)" strokeWidth={1.5} />
      <line x1={162} y1={72} x2={174} y2={70} stroke="var(--border-default)" strokeWidth={1.5} />
      <rect x={173} y={66} width={11} height={8} rx={2} fill="none" stroke="var(--border-default)" strokeWidth={1.5} />
      {/* Center console */}
      <rect
        x={90}
        y={85}
        width={20}
        height={90}
        rx={4}
        fill="var(--surface-sunken)"
        stroke="var(--border-muted)"
        strokeWidth={1}
        opacity={0.45}
      />
      {/* Steering wheel (driver/left) */}
      <circle cx={70} cy={100} r={13} fill="none" stroke="var(--border-default)" strokeWidth={1.5} opacity={0.4} />
      <line x1={70} y1={89} x2={70} y2={111} stroke="var(--border-default)" strokeWidth={1} opacity={0.25} />
      <line x1={59} y1={100} x2={81} y2={100} stroke="var(--border-default)" strokeWidth={1} opacity={0.25} />
      {/* Wheel arches (front — pronounced fender flares) */}
      <path d="M 33 62 Q 26 80 33 98" fill="none" stroke="var(--border-muted)" strokeWidth={1.5} opacity={0.4} />
      <path d="M 167 62 Q 174 80 167 98" fill="none" stroke="var(--border-muted)" strokeWidth={1.5} opacity={0.4} />
      {/* Wheel arches (rear — pronounced) */}
      <path d="M 33 210 Q 26 230 33 250" fill="none" stroke="var(--border-muted)" strokeWidth={1.5} opacity={0.4} />
      <path d="M 167 210 Q 174 230 167 250" fill="none" stroke="var(--border-muted)" strokeWidth={1.5} opacity={0.4} />
      {/* Light bar (roof, police interceptor style) */}
      <rect
        x={55}
        y={78}
        width={90}
        height={5}
        rx={2.5}
        fill="var(--surface-sunken)"
        stroke="var(--border-muted)"
        strokeWidth={0.8}
        opacity={0.45}
      />
      {/* Headlights (swept back, LED style) */}
      <path d="M 44 22 L 58 18 L 56 28 Z" fill="var(--color-warning-400, #FFD54F)" opacity={0.25} />
      <path d="M 156 22 L 142 18 L 144 28 Z" fill="var(--color-warning-400, #FFD54F)" opacity={0.25} />
      {/* Front grille (wide, modern Explorer) */}
      <rect
        x={62}
        y={14}
        width={76}
        height={7}
        rx={3}
        fill="none"
        stroke="var(--border-muted)"
        strokeWidth={1}
        opacity={0.3}
      />
      {/* Front bumper lower */}
      <path
        d="M 50 28 L 62 24 L 138 24 L 150 28"
        fill="none"
        stroke="var(--border-muted)"
        strokeWidth={0.8}
        opacity={0.25}
      />
      {/* Taillights (wrap-around, modern Explorer) */}
      <path
        d="M 44 260 L 48 270 L 44 275"
        fill="none"
        stroke="var(--color-danger-400, #EF5350)"
        strokeWidth={2}
        opacity={0.35}
      />
      <path
        d="M 156 260 L 152 270 L 156 275"
        fill="none"
        stroke="var(--color-danger-400, #EF5350)"
        strokeWidth={2}
        opacity={0.35}
      />
      {/* Rear bumper */}
      <path d="M 52 284 Q 100 290 148 284" fill="none" stroke="var(--border-muted)" strokeWidth={1} opacity={0.25} />
      {/* Rear spoiler/lip */}
      <line x1={55} y1={215} x2={145} y2={215} stroke="var(--border-muted)" strokeWidth={1} opacity={0.2} />

      {/* Seat positions */}
      {renderSeats(props, 20)}
    </svg>
  );
}

// ── Seagrave Engine (Marauder II Pumper) ─────────────────────────────

function EngineSVG(props: VehicleSVGProps) {
  return (
    <svg viewBox="0 0 220 540" className="w-full max-w-[220px]" role="img" aria-label="Seagrave Engine top-down view">
      <SeagraveCab />
      <SeagraveCrewCompartment />

      {/* === Body section (pump panel, compartments, hose bed) === */}
      <rect
        x={35}
        y={285}
        width={150}
        height={230}
        rx={3}
        fill="none"
        stroke="var(--border-strong)"
        strokeWidth={2.5}
      />
      <line x1={35} y1={285} x2={185} y2={285} stroke="var(--border-strong)" strokeWidth={2} />
      {/* Pump panel area */}
      <rect
        x={45}
        y={295}
        width={130}
        height={30}
        rx={2}
        fill="var(--surface-sunken)"
        stroke="var(--border-muted)"
        strokeWidth={1}
        opacity={0.3}
      />
      <text
        x={110}
        y={314}
        textAnchor="middle"
        fill="var(--text-disabled)"
        fontSize={8}
        fontFamily="var(--font-sans)"
        opacity={0.5}
      >
        PUMP PANEL
      </text>
      {/* Compartment lines */}
      <line x1={35} y1={340} x2={185} y2={340} stroke="var(--border-muted)" strokeWidth={1} opacity={0.3} />
      <line x1={35} y1={400} x2={185} y2={400} stroke="var(--border-muted)" strokeWidth={1} opacity={0.3} />
      {/* Compartment door handles (driver side) */}
      <line x1={37} y1={355} x2={37} y2={365} stroke="var(--border-muted)" strokeWidth={0.8} opacity={0.25} />
      <line x1={37} y1={410} x2={37} y2={420} stroke="var(--border-muted)" strokeWidth={0.8} opacity={0.25} />
      {/* Compartment door handles (passenger side) */}
      <line x1={183} y1={355} x2={183} y2={365} stroke="var(--border-muted)" strokeWidth={0.8} opacity={0.25} />
      <line x1={183} y1={410} x2={183} y2={420} stroke="var(--border-muted)" strokeWidth={0.8} opacity={0.25} />
      {/* Hose bed */}
      <rect
        x={50}
        y={415}
        width={120}
        height={85}
        rx={3}
        fill="var(--surface-sunken)"
        stroke="var(--border-muted)"
        strokeWidth={1}
        opacity={0.25}
      />
      <text
        x={110}
        y={462}
        textAnchor="middle"
        fill="var(--text-disabled)"
        fontSize={8}
        fontFamily="var(--font-sans)"
        opacity={0.5}
      >
        HOSE BED
      </text>
      {/* Wheel wells — rear (body) */}
      <path d="M 35 450 Q 28 470 35 490" fill="none" stroke="var(--border-muted)" strokeWidth={1.2} opacity={0.35} />
      <path d="M 185 450 Q 192 470 185 490" fill="none" stroke="var(--border-muted)" strokeWidth={1.2} opacity={0.35} />
      {/* Taillights */}
      <rect x={40} y={510} width={14} height={5} rx={2} fill="var(--color-danger-400, #EF5350)" opacity={0.3} />
      <rect x={166} y={510} width={14} height={5} rx={2} fill="var(--color-danger-400, #EF5350)" opacity={0.3} />
      {/* Rear step / tailboard */}
      <rect
        x={45}
        y={513}
        width={130}
        height={5}
        rx={1.5}
        fill="none"
        stroke="var(--border-muted)"
        strokeWidth={0.8}
        opacity={0.3}
      />

      {renderSeats(props, 18)}
    </svg>
  );
}

// ── Seagrave Aerialscope Tower ───────────────────────────────────────

function TowerSVG(props: VehicleSVGProps) {
  return (
    <svg viewBox="0 0 220 700" className="w-full max-w-[220px]" role="img" aria-label="Seagrave Tower top-down view">
      <SeagraveCab />
      <SeagraveCrewCompartment />

      {/* === Turntable & aerial boom section === */}
      <rect
        x={35}
        y={285}
        width={150}
        height={390}
        rx={3}
        fill="none"
        stroke="var(--border-strong)"
        strokeWidth={2.5}
      />
      <line x1={35} y1={285} x2={185} y2={285} stroke="var(--border-strong)" strokeWidth={2} />

      {/* Turntable (circular rotating base for aerial) */}
      <circle cx={110} cy={345} r={45} fill="none" stroke="var(--border-default)" strokeWidth={2} opacity={0.4} />
      <circle cx={110} cy={345} r={8} fill="none" stroke="var(--border-muted)" strokeWidth={1.5} opacity={0.35} />
      {/* Turntable cross-hairs */}
      <line x1={110} y1={305} x2={110} y2={385} stroke="var(--border-muted)" strokeWidth={0.8} opacity={0.2} />
      <line x1={70} y1={345} x2={150} y2={345} stroke="var(--border-muted)" strokeWidth={0.8} opacity={0.2} />
      <text
        x={110}
        y={400}
        textAnchor="middle"
        fill="var(--text-disabled)"
        fontSize={7}
        fontFamily="var(--font-sans)"
        opacity={0.45}
      >
        TURNTABLE
      </text>

      {/* Aerial boom (extends along body centerline toward rear) */}
      <rect
        x={96}
        y={410}
        width={28}
        height={210}
        rx={4}
        fill="var(--surface-sunken)"
        stroke="var(--border-muted)"
        strokeWidth={1.2}
        opacity={0.3}
      />
      {/* Boom section lines (telescoping segments) */}
      <line x1={98} y1={470} x2={122} y2={470} stroke="var(--border-muted)" strokeWidth={0.8} opacity={0.3} />
      <line x1={98} y1={530} x2={122} y2={530} stroke="var(--border-muted)" strokeWidth={0.8} opacity={0.3} />
      <line x1={98} y1={580} x2={122} y2={580} stroke="var(--border-muted)" strokeWidth={0.8} opacity={0.3} />
      <text
        x={110}
        y={450}
        textAnchor="middle"
        fill="var(--text-disabled)"
        fontSize={6}
        fontFamily="var(--font-sans)"
        opacity={0.4}
      >
        BOOM
      </text>

      {/* Platform bucket (at rear of boom) */}
      <rect
        x={90}
        y={622}
        width={40}
        height={24}
        rx={5}
        fill="var(--surface-sunken)"
        stroke="var(--border-default)"
        strokeWidth={1.5}
        opacity={0.4}
      />
      <text
        x={110}
        y={637}
        textAnchor="middle"
        fill="var(--text-disabled)"
        fontSize={6}
        fontFamily="var(--font-sans)"
        opacity={0.45}
      >
        PLATFORM
      </text>

      {/* Outrigger / stabilizer jacks (4 total — 2 front, 2 rear) */}
      {/* Front outriggers (just behind turntable) */}
      <line x1={35} y1={305} x2={18} y2={305} stroke="var(--border-default)" strokeWidth={1.5} opacity={0.35} />
      <rect
        x={10}
        y={300}
        width={10}
        height={10}
        rx={2}
        fill="none"
        stroke="var(--border-default)"
        strokeWidth={1.2}
        opacity={0.35}
      />
      <line x1={185} y1={305} x2={202} y2={305} stroke="var(--border-default)" strokeWidth={1.5} opacity={0.35} />
      <rect
        x={200}
        y={300}
        width={10}
        height={10}
        rx={2}
        fill="none"
        stroke="var(--border-default)"
        strokeWidth={1.2}
        opacity={0.35}
      />
      {/* Rear outriggers (near rear axle) */}
      <line x1={35} y1={620} x2={18} y2={620} stroke="var(--border-default)" strokeWidth={1.5} opacity={0.35} />
      <rect
        x={10}
        y={615}
        width={10}
        height={10}
        rx={2}
        fill="none"
        stroke="var(--border-default)"
        strokeWidth={1.2}
        opacity={0.35}
      />
      <line x1={185} y1={620} x2={202} y2={620} stroke="var(--border-default)" strokeWidth={1.5} opacity={0.35} />
      <rect
        x={200}
        y={615}
        width={10}
        height={10}
        rx={2}
        fill="none"
        stroke="var(--border-default)"
        strokeWidth={1.2}
        opacity={0.35}
      />

      {/* Compartments (sides, smaller — equipment storage) */}
      <line x1={35} y1={410} x2={95} y2={410} stroke="var(--border-muted)" strokeWidth={1} opacity={0.25} />
      <line x1={125} y1={410} x2={185} y2={410} stroke="var(--border-muted)" strokeWidth={1} opacity={0.25} />
      {/* Compartment door handles */}
      <line x1={37} y1={420} x2={37} y2={430} stroke="var(--border-muted)" strokeWidth={0.8} opacity={0.25} />
      <line x1={183} y1={420} x2={183} y2={430} stroke="var(--border-muted)" strokeWidth={0.8} opacity={0.25} />

      {/* Wheel wells — rear (tandem axle) */}
      <path d="M 35 600 Q 28 620 35 640" fill="none" stroke="var(--border-muted)" strokeWidth={1.2} opacity={0.35} />
      <path d="M 185 600 Q 192 620 185 640" fill="none" stroke="var(--border-muted)" strokeWidth={1.2} opacity={0.35} />
      {/* Second rear axle */}
      <path d="M 35 640 Q 28 658 35 675" fill="none" stroke="var(--border-muted)" strokeWidth={1.2} opacity={0.3} />
      <path d="M 185 640 Q 192 658 185 675" fill="none" stroke="var(--border-muted)" strokeWidth={1.2} opacity={0.3} />

      {/* Taillights */}
      <rect x={40} y={670} width={14} height={5} rx={2} fill="var(--color-danger-400, #EF5350)" opacity={0.3} />
      <rect x={166} y={670} width={14} height={5} rx={2} fill="var(--color-danger-400, #EF5350)" opacity={0.3} />

      {renderSeats(props, 18)}
    </svg>
  );
}

// ── Seagrave T200CT Walk-In Heavy Rescue ─────────────────────────────

function RescueSVG(props: VehicleSVGProps) {
  return (
    <svg
      viewBox="0 0 220 620"
      className="w-full max-w-[220px]"
      role="img"
      aria-label="Seagrave Walk-In Rescue top-down view"
    >
      <SeagraveCab />
      <SeagraveCrewCompartment />

      {/* === Walk-in rescue body (T200CT, #3CR12 S/S, 273" body) === */}
      {/* Body is wider than engine — full walk-in interior */}
      <rect
        x={30}
        y={285}
        width={160}
        height={310}
        rx={3}
        fill="none"
        stroke="var(--border-strong)"
        strokeWidth={2.5}
      />
      <line x1={30} y1={285} x2={190} y2={285} stroke="var(--border-strong)" strokeWidth={2} />

      {/* Forward compartments — driver side (2 external, roll-up doors) */}
      <rect
        x={32}
        y={295}
        width={8}
        height={60}
        rx={1.5}
        fill="none"
        stroke="var(--border-muted)"
        strokeWidth={1}
        opacity={0.35}
      />
      <line x1={36} y1={298} x2={36} y2={352} stroke="var(--border-muted)" strokeWidth={0.6} opacity={0.25} />
      <rect
        x={32}
        y={365}
        width={8}
        height={60}
        rx={1.5}
        fill="none"
        stroke="var(--border-muted)"
        strokeWidth={1}
        opacity={0.35}
      />
      <line x1={36} y1={368} x2={36} y2={422} stroke="var(--border-muted)" strokeWidth={0.6} opacity={0.25} />
      {/* Passenger side — 2 external compartments */}
      <rect
        x={180}
        y={295}
        width={8}
        height={60}
        rx={1.5}
        fill="none"
        stroke="var(--border-muted)"
        strokeWidth={1}
        opacity={0.35}
      />
      <line x1={184} y1={298} x2={184} y2={352} stroke="var(--border-muted)" strokeWidth={0.6} opacity={0.25} />
      <rect
        x={180}
        y={365}
        width={8}
        height={60}
        rx={1.5}
        fill="none"
        stroke="var(--border-muted)"
        strokeWidth={1}
        opacity={0.35}
      />
      <line x1={184} y1={368} x2={184} y2={422} stroke="var(--border-muted)" strokeWidth={0.6} opacity={0.25} />

      {/* Compartment dividers */}
      <line x1={30} y1={360} x2={190} y2={360} stroke="var(--border-muted)" strokeWidth={1} opacity={0.25} />

      {/* Walk-in interior section (rear half of body) */}
      <line x1={30} y1={435} x2={190} y2={435} stroke="var(--border-default)" strokeWidth={1.5} opacity={0.35} />
      <rect
        x={48}
        y={442}
        width={124}
        height={100}
        rx={3}
        fill="var(--surface-sunken)"
        stroke="var(--border-muted)"
        strokeWidth={1}
        opacity={0.2}
      />
      <text
        x={110}
        y={480}
        textAnchor="middle"
        fill="var(--text-disabled)"
        fontSize={8}
        fontFamily="var(--font-sans)"
        opacity={0.45}
      >
        WALK-IN
      </text>
      <text
        x={110}
        y={492}
        textAnchor="middle"
        fill="var(--text-disabled)"
        fontSize={6}
        fontFamily="var(--font-sans)"
        opacity={0.35}
      >
        EQUIPMENT STORAGE
      </text>
      {/* Interior benches (both sides of walk-in) */}
      <rect
        x={50}
        y={445}
        width={12}
        height={90}
        rx={2}
        fill="var(--surface-sunken)"
        stroke="var(--border-muted)"
        strokeWidth={0.8}
        opacity={0.15}
      />
      <rect
        x={158}
        y={445}
        width={12}
        height={90}
        rx={2}
        fill="var(--surface-sunken)"
        stroke="var(--border-muted)"
        strokeWidth={0.8}
        opacity={0.15}
      />

      {/* Walk-in body side door (driver side) */}
      <rect
        x={30}
        y={445}
        width={4}
        height={40}
        rx={1}
        fill="none"
        stroke="var(--border-default)"
        strokeWidth={1.2}
        opacity={0.35}
      />
      <circle cx={32} cy={465} r={2} fill="none" stroke="var(--border-muted)" strokeWidth={0.8} opacity={0.3} />

      {/* Rear doors (double swing-out) */}
      <line x1={80} y1={593} x2={80} y2={595} stroke="var(--border-default)" strokeWidth={1.2} opacity={0.4} />
      <line x1={140} y1={593} x2={140} y2={595} stroke="var(--border-default)" strokeWidth={1.2} opacity={0.4} />
      <path d="M 80 595 L 110 600 L 140 595" fill="none" stroke="var(--border-muted)" strokeWidth={1} opacity={0.35} />

      {/* Light tower (Will-Burt Nightscan, roof-mounted) */}
      <rect
        x={85}
        y={300}
        width={50}
        height={18}
        rx={3}
        fill="var(--surface-sunken)"
        stroke="var(--border-muted)"
        strokeWidth={1}
        opacity={0.3}
      />
      <text
        x={110}
        y={313}
        textAnchor="middle"
        fill="var(--text-disabled)"
        fontSize={5.5}
        fontFamily="var(--font-sans)"
        opacity={0.4}
      >
        LIGHT TOWER
      </text>

      {/* Front winch (Ramsey RE12000, bumper-mounted) */}
      <rect
        x={95}
        y={1}
        width={30}
        height={6}
        rx={2}
        fill="none"
        stroke="var(--border-muted)"
        strokeWidth={0.8}
        opacity={0.3}
      />

      {/* Generator (Onan 30kW, under body) */}
      <rect
        x={70}
        y={545}
        width={80}
        height={16}
        rx={2}
        fill="var(--surface-sunken)"
        stroke="var(--border-muted)"
        strokeWidth={0.8}
        opacity={0.2}
      />
      <text
        x={110}
        y={556}
        textAnchor="middle"
        fill="var(--text-disabled)"
        fontSize={5}
        fontFamily="var(--font-sans)"
        opacity={0.35}
      >
        30kW GENERATOR
      </text>

      {/* Wheel wells — rear (tandem axle, DANA D26A4) */}
      <path d="M 30 520 Q 23 538 30 555" fill="none" stroke="var(--border-muted)" strokeWidth={1.2} opacity={0.35} />
      <path d="M 190 520 Q 197 538 190 555" fill="none" stroke="var(--border-muted)" strokeWidth={1.2} opacity={0.35} />
      {/* Second rear axle */}
      <path d="M 30 555 Q 23 570 30 585" fill="none" stroke="var(--border-muted)" strokeWidth={1.2} opacity={0.3} />
      <path d="M 190 555 Q 197 570 190 585" fill="none" stroke="var(--border-muted)" strokeWidth={1.2} opacity={0.3} />

      {/* Taillights */}
      <rect x={35} y={590} width={14} height={5} rx={2} fill="var(--color-danger-400, #EF5350)" opacity={0.3} />
      <rect x={171} y={590} width={14} height={5} rx={2} fill="var(--color-danger-400, #EF5350)" opacity={0.3} />
      {/* Rear step / tailboard */}
      <rect
        x={45}
        y={595}
        width={130}
        height={6}
        rx={1.5}
        fill="none"
        stroke="var(--border-muted)"
        strokeWidth={0.8}
        opacity={0.3}
      />
      {/* Receiver hitch */}
      <rect
        x={103}
        y={601}
        width={14}
        height={6}
        rx={1}
        fill="none"
        stroke="var(--border-muted)"
        strokeWidth={0.8}
        opacity={0.25}
      />

      {renderSeats(props, 18)}
    </svg>
  );
}

// ── F350 Ambulance with Horton Type C Box ─────────────────────────────

function AmbulanceSVG(props: VehicleSVGProps) {
  return (
    <svg viewBox="0 0 220 480" className="w-full max-w-[220px]" role="img" aria-label="Ambulance top-down view">
      {/* === Cab (F350) === */}
      <path
        d="M 45 20 Q 45 5 60 5 L 160 5 Q 175 5 175 20
           L 178 90 L 178 125 L 42 125 L 42 90 Z"
        fill="none"
        stroke="var(--border-strong)"
        strokeWidth={2.5}
        strokeLinejoin="round"
      />
      {/* Windshield */}
      <path
        d="M 52 52 L 58 35 L 162 35 L 168 52 Z"
        fill="var(--surface-sunken)"
        stroke="var(--border-default)"
        strokeWidth={1.5}
        opacity={0.5}
      />
      {/* Side mirrors — rectangular on stalks */}
      <line x1={42} y1={46} x2={30} y2={48} stroke="var(--border-default)" strokeWidth={1.5} />
      <rect x={14} y={42} width={16} height={11} rx={2} fill="none" stroke="var(--border-default)" strokeWidth={1.5} />
      <line x1={178} y1={46} x2={190} y2={48} stroke="var(--border-default)" strokeWidth={1.5} />
      <rect x={190} y={42} width={16} height={11} rx={2} fill="none" stroke="var(--border-default)" strokeWidth={1.5} />
      {/* Steering wheel */}
      <circle cx={78} cy={80} r={12} fill="none" stroke="var(--border-default)" strokeWidth={1.5} opacity={0.35} />
      {/* Center console */}
      <rect
        x={100}
        y={65}
        width={20}
        height={45}
        rx={3}
        fill="var(--surface-sunken)"
        stroke="var(--border-muted)"
        strokeWidth={1}
        opacity={0.4}
      />
      {/* Headlights */}
      <rect x={50} y={5} width={14} height={5} rx={2} fill="var(--color-warning-400, #FFD54F)" opacity={0.3} />
      <rect x={156} y={5} width={14} height={5} rx={2} fill="var(--color-warning-400, #FFD54F)" opacity={0.3} />

      {/* === Box (Horton Type C) === */}
      <rect
        x={30}
        y={125}
        width={160}
        height={330}
        rx={4}
        fill="none"
        stroke="var(--border-strong)"
        strokeWidth={2.5}
      />
      {/* Box divider from cab */}
      <line x1={30} y1={125} x2={190} y2={125} stroke="var(--border-strong)" strokeWidth={2.5} />
      {/* Star of Life indicator (subtle) */}
      <text
        x={110}
        y={148}
        textAnchor="middle"
        fill="var(--text-disabled)"
        fontSize={9}
        fontFamily="var(--font-sans)"
        opacity={0.4}
      >
        PATIENT COMPARTMENT
      </text>
      {/* Stretcher (center) */}
      <rect
        x={82}
        y={185}
        width={56}
        height={200}
        rx={6}
        fill="var(--surface-sunken)"
        stroke="var(--border-muted)"
        strokeWidth={1.5}
        opacity={0.4}
      />
      <text
        x={110}
        y={290}
        textAnchor="middle"
        fill="var(--text-disabled)"
        fontSize={8}
        fontFamily="var(--font-sans)"
        opacity={0.4}
      >
        STRETCHER
      </text>
      {/* Bench seat (passenger/right side) */}
      <rect
        x={152}
        y={175}
        width={22}
        height={130}
        rx={3}
        fill="var(--surface-sunken)"
        stroke="var(--border-muted)"
        strokeWidth={1}
        opacity={0.3}
      />
      <text
        x={163}
        y={245}
        textAnchor="middle"
        fill="var(--text-disabled)"
        fontSize={6}
        fontFamily="var(--font-sans)"
        opacity={0.4}
        transform="rotate(90, 163, 245)"
      >
        BENCH
      </text>
      {/* Captain's chair indicator (rear-facing, at head of stretcher on passenger side) */}
      <path
        d="M 145 195 L 145 205 M 141 201 L 145 207 L 149 201"
        stroke="var(--text-disabled)"
        strokeWidth={1}
        fill="none"
        opacity={0.4}
      />
      {/* Driver-side seat indicator */}
      <path
        d="M 56 260 L 62 260 M 59 256 L 65 260 L 59 264"
        stroke="var(--text-disabled)"
        strokeWidth={1}
        fill="none"
        opacity={0.4}
      />
      {/* Wheel wells — front (cab) */}
      <path d="M 42 80 Q 36 94 42 108" fill="none" stroke="var(--border-muted)" strokeWidth={1.2} opacity={0.35} />
      <path d="M 178 80 Q 184 94 178 108" fill="none" stroke="var(--border-muted)" strokeWidth={1.2} opacity={0.35} />
      {/* Wheel wells — rear (box, dual rear axle) */}
      <path d="M 30 385 Q 24 400 30 415" fill="none" stroke="var(--border-muted)" strokeWidth={1.2} opacity={0.35} />
      <path d="M 190 385 Q 196 400 190 415" fill="none" stroke="var(--border-muted)" strokeWidth={1.2} opacity={0.35} />
      {/* Light bar (cab roof) */}
      <rect
        x={55}
        y={55}
        width={110}
        height={4}
        rx={2}
        fill="var(--surface-sunken)"
        stroke="var(--border-muted)"
        strokeWidth={0.8}
        opacity={0.4}
      />
      {/* Pass-through compartment outlines (both sides of box) */}
      <rect
        x={32}
        y={160}
        width={8}
        height={60}
        rx={1}
        fill="none"
        stroke="var(--border-muted)"
        strokeWidth={0.8}
        opacity={0.25}
      />
      <rect
        x={180}
        y={160}
        width={8}
        height={60}
        rx={1}
        fill="none"
        stroke="var(--border-muted)"
        strokeWidth={0.8}
        opacity={0.25}
      />
      {/* Rear doors */}
      <line x1={85} y1={453} x2={85} y2={455} stroke="var(--border-default)" strokeWidth={1} opacity={0.5} />
      <line x1={135} y1={453} x2={135} y2={455} stroke="var(--border-default)" strokeWidth={1} opacity={0.5} />
      <path d="M 85 455 L 110 460 L 135 455" fill="none" stroke="var(--border-muted)" strokeWidth={1} opacity={0.4} />
      {/* Rear step bumper */}
      <rect
        x={45}
        y={458}
        width={130}
        height={5}
        rx={1.5}
        fill="none"
        stroke="var(--border-muted)"
        strokeWidth={0.8}
        opacity={0.3}
      />
      {/* Taillights */}
      <rect x={35} y={450} width={14} height={5} rx={2} fill="var(--color-danger-400, #EF5350)" opacity={0.3} />
      <rect x={171} y={450} width={14} height={5} rx={2} fill="var(--color-danger-400, #EF5350)" opacity={0.3} />

      {renderSeats(props, 18)}
    </svg>
  );
}

// ── F350 Pickup with Pump Sled (Brush 518) ────────────────────────────

function BrushSVG(props: VehicleSVGProps) {
  return (
    <svg viewBox="0 0 220 310" className="w-full max-w-[220px]" role="img" aria-label="Brush truck top-down view">
      {/* === Cab (F350 — same platform as Ambulance) === */}
      <path
        d="M 45 20 Q 45 5 60 5 L 160 5 Q 175 5 175 20
           L 178 90 L 178 125 L 42 125 L 42 90 Z"
        fill="none"
        stroke="var(--border-strong)"
        strokeWidth={2.5}
        strokeLinejoin="round"
      />
      {/* Windshield */}
      <path
        d="M 52 52 L 58 35 L 162 35 L 168 52 Z"
        fill="var(--surface-sunken)"
        stroke="var(--border-default)"
        strokeWidth={1.5}
        opacity={0.5}
      />
      {/* Side mirrors — rectangular on stalks */}
      <line x1={42} y1={46} x2={30} y2={48} stroke="var(--border-default)" strokeWidth={1.5} />
      <rect x={14} y={42} width={16} height={11} rx={2} fill="none" stroke="var(--border-default)" strokeWidth={1.5} />
      <line x1={178} y1={46} x2={190} y2={48} stroke="var(--border-default)" strokeWidth={1.5} />
      <rect x={190} y={42} width={16} height={11} rx={2} fill="none" stroke="var(--border-default)" strokeWidth={1.5} />
      {/* Steering wheel */}
      <circle cx={78} cy={80} r={12} fill="none" stroke="var(--border-default)" strokeWidth={1.5} opacity={0.35} />
      {/* Center console */}
      <rect
        x={100}
        y={65}
        width={20}
        height={45}
        rx={3}
        fill="var(--surface-sunken)"
        stroke="var(--border-muted)"
        strokeWidth={1}
        opacity={0.4}
      />
      {/* Headlights */}
      <rect x={50} y={5} width={14} height={5} rx={2} fill="var(--color-warning-400, #FFD54F)" opacity={0.3} />
      <rect x={156} y={5} width={14} height={5} rx={2} fill="var(--color-warning-400, #FFD54F)" opacity={0.3} />
      {/* Light bar (cab roof) */}
      <rect
        x={55}
        y={55}
        width={110}
        height={4}
        rx={2}
        fill="var(--surface-sunken)"
        stroke="var(--border-muted)"
        strokeWidth={0.8}
        opacity={0.4}
      />

      {/* === Pickup Bed === */}
      <rect
        x={38}
        y={125}
        width={144}
        height={145}
        rx={3}
        fill="none"
        stroke="var(--border-strong)"
        strokeWidth={2.5}
      />
      {/* Bed divider from cab */}
      <line x1={38} y1={125} x2={182} y2={125} stroke="var(--border-strong)" strokeWidth={2.5} />
      {/* Bed rail lines (inner walls) */}
      <line x1={44} y1={130} x2={44} y2={265} stroke="var(--border-muted)" strokeWidth={0.8} opacity={0.2} />
      <line x1={176} y1={130} x2={176} y2={265} stroke="var(--border-muted)" strokeWidth={0.8} opacity={0.2} />

      {/* === Pump Sled === */}
      <rect
        x={55}
        y={140}
        width={110}
        height={100}
        rx={4}
        fill="var(--surface-sunken)"
        stroke="var(--border-muted)"
        strokeWidth={1.5}
        opacity={0.45}
      />
      <text
        x={110}
        y={195}
        textAnchor="middle"
        fill="var(--text-disabled)"
        fontSize={9}
        fontFamily="var(--font-sans)"
        opacity={0.4}
      >
        PUMP SLED
      </text>
      {/* Pump discharge fittings */}
      <circle cx={60} cy={250} r={4} fill="none" stroke="var(--border-muted)" strokeWidth={1} opacity={0.3} />
      <circle cx={160} cy={250} r={4} fill="none" stroke="var(--border-muted)" strokeWidth={1} opacity={0.3} />

      {/* Front wheel wells (cab) */}
      <path d="M 42 80 Q 36 94 42 108" fill="none" stroke="var(--border-muted)" strokeWidth={1.2} opacity={0.35} />
      <path d="M 178 80 Q 184 94 178 108" fill="none" stroke="var(--border-muted)" strokeWidth={1.2} opacity={0.35} />
      {/* Rear wheel wells (bed) */}
      <path d="M 38 220 Q 32 235 38 250" fill="none" stroke="var(--border-muted)" strokeWidth={1.2} opacity={0.35} />
      <path d="M 182 220 Q 188 235 182 250" fill="none" stroke="var(--border-muted)" strokeWidth={1.2} opacity={0.35} />

      {/* Taillights */}
      <rect x={43} y={265} width={14} height={5} rx={2} fill="var(--color-danger-400, #EF5350)" opacity={0.3} />
      <rect x={163} y={265} width={14} height={5} rx={2} fill="var(--color-danger-400, #EF5350)" opacity={0.3} />
      {/* Rear bumper */}
      <rect
        x={48}
        y={272}
        width={124}
        height={5}
        rx={1.5}
        fill="none"
        stroke="var(--border-muted)"
        strokeWidth={0.8}
        opacity={0.3}
      />

      {renderSeats(props, 18)}
    </svg>
  );
}

// ── Seat position layouts per unit type ───────────────────────────────

/** BC Buggy: FIST = driver (left), BC = passenger (right) */
const BUGGY_SEATS: SeatPos[] = [
  { seatId: 'fist', x: 70, y: 140 }, // driver (left)
  { seatId: 'bc', x: 130, y: 140 }, // passenger/officer (right)
];

/** Engine: DPO driver, IO passenger, rear crew */
const ENGINE_SEAT_POSITIONS: SeatPos[] = [
  { seatId: 'dpo', x: 72, y: 85 }, // driver (left front)
  { seatId: 'io', x: 148, y: 85 }, // officer/passenger (right front)
  { seatId: 'ff_1', x: 72, y: 165 }, // behind driver (rear-facing)
  { seatId: 'lead_ff', x: 148, y: 165 }, // behind officer (rear-facing) — LFF
  { seatId: 'ff_2', x: 72, y: 248 }, // south wall left (forward-facing)
  { seatId: 'ff_3', x: 148, y: 248 }, // south wall right (forward-facing)
];

/** Tower: AO driver, IO passenger, rear crew same as engine */
const TOWER_SEAT_POSITIONS: SeatPos[] = [
  { seatId: 'ao', x: 72, y: 85 }, // driver (left front) — Aerial Operator
  { seatId: 'io', x: 148, y: 85 }, // officer/passenger (right front)
  { seatId: 'ff_1', x: 72, y: 165 }, // behind driver (rear-facing)
  { seatId: 'lead_ff', x: 148, y: 165 }, // behind officer (rear-facing) — LFF
  { seatId: 'ff_2', x: 72, y: 248 }, // south wall left (forward-facing)
  { seatId: 'ff_3', x: 148, y: 248 }, // south wall right (forward-facing)
];

/** Rescue: DO driver, IO passenger, rear crew same as engine */
const RESCUE_SEAT_POSITIONS: SeatPos[] = [
  { seatId: 'do', x: 72, y: 85 }, // driver (left front) — Driver/Operator
  { seatId: 'io', x: 148, y: 85 }, // officer/passenger (right front)
  { seatId: 'ff_1', x: 72, y: 165 }, // behind driver (rear-facing)
  { seatId: 'lead_ff', x: 148, y: 165 }, // behind officer (rear-facing) — LFF
  { seatId: 'ff_2', x: 72, y: 248 }, // south wall left (forward-facing)
  { seatId: 'ff_3', x: 148, y: 248 }, // south wall right (forward-facing)
];

/** Ambulance: driver left, lead_tech right, att_1 captain's chair (passenger side), att_2 driver side */
const AMBULANCE_SEAT_POSITIONS: SeatPos[] = [
  { seatId: 'driver', x: 78, y: 82 }, // driver (left front)
  { seatId: 'lead_tech', x: 142, y: 82 }, // passenger (right front) — Lead Tech
  { seatId: 'att_1', x: 145, y: 195 }, // captain's chair (rear-facing, passenger side)
  { seatId: 'att_2', x: 56, y: 260 }, // single seat (driver side, facing stretcher)
];

/** Brush 518: Lead FF driver (left), FF passenger (right) — F350 cab only */
const BRUSH_SEAT_POSITIONS: SeatPos[] = [
  { seatId: 'lead_ff', x: 78, y: 82 }, // driver (left front) — Lead Firefighter
  { seatId: 'ff_1', x: 142, y: 82 }, // passenger (right front) — Firefighter
];

function getSeatPositions(type: UnitDef['type']): SeatPos[] {
  switch (type) {
    case 'buggy':
      return BUGGY_SEATS;
    case 'engine':
      return ENGINE_SEAT_POSITIONS;
    case 'tower':
      return TOWER_SEAT_POSITIONS;
    case 'rescue':
      return RESCUE_SEAT_POSITIONS;
    case 'ambulance':
    case 'medic':
      return AMBULANCE_SEAT_POSITIONS;
    case 'brush':
      return BRUSH_SEAT_POSITIONS;
    default:
      return [];
  }
}

// ── Unit type label ───────────────────────────────────────────────────

function getVehicleLabel(type: UnitDef['type']): string {
  switch (type) {
    case 'buggy':
      return 'Ford Explorer';
    case 'engine':
      return 'Seagrave Engine';
    case 'tower':
      return 'Seagrave Aerialscope';
    case 'rescue':
      return 'Seagrave Heavy Rescue';
    case 'ambulance':
    case 'medic':
      return 'F350 / Horton';
    case 'brush':
      return 'F350 / Pump Sled';
    default:
      return '';
  }
}

// ── Unit type colors ──────────────────────────────────────────────────

function getUnitColor(type: UnitDef['type']): string {
  switch (type) {
    case 'engine':
      return '#EF5350';
    case 'ambulance':
      return '#42A5F5';
    case 'medic':
      return '#AB47BC';
    case 'tower':
      return '#FFA726';
    case 'rescue':
      return '#66BB6A';
    case 'brush':
      return '#8D6E63';
    case 'buggy':
      return '#FFD54F';
    default:
      return '#999';
  }
}

// ── Main Apparatus Card ───────────────────────────────────────────────

export default function ApparatusCard({
  unit,
  battalionId,
  getAssignment,
  getPendingChange,
  canEdit,
  onEditSeat,
  memberPhotos,
}: ApparatusCardProps) {
  const unitColor = getUnitColor(unit.type);
  const seatPositions = getSeatPositions(unit.type);
  const filledCount = unit.seats.filter((s) => getAssignment(battalionId, unit.id, s.id)).length;
  const minStaffing = unit.seats.filter((s) => s.required).length;
  const requiredFilled = unit.seats
    .filter((s) => s.required)
    .filter((s) => getAssignment(battalionId, unit.id, s.id)).length;
  const meetsMinimum = requiredFilled >= minStaffing;

  const commonProps = {
    seats: seatPositions,
    battalionId,
    unitId: unit.id,
    getAssignment,
    getPendingChange,
    canEdit,
    onEditSeat,
    seatDefs: unit.seats,
    memberPhotos,
  };

  return (
    <div className="flex flex-col items-center bg-surface-base rounded-xl border border-edge-muted p-4 gap-2">
      {/* Unit header */}
      <div className="flex items-center gap-2 w-full">
        <span className="text-xs font-bold uppercase tracking-wide" style={{ color: unitColor }}>
          {unit.id}
        </span>
        <span className="text-[10px] text-content-tertiary flex-1">{unit.label}</span>
        <div className="flex items-center gap-1.5">
          {!meetsMinimum && <AlertTriangle className="h-3 w-3 text-warning-400" aria-label="Below minimum staffing" />}
          <span className="text-[10px] text-content-tertiary">
            {filledCount}/{unit.seats.length}
          </span>
        </div>
      </div>

      {/* Vehicle type subtitle */}
      <div className="text-[9px] text-content-disabled uppercase tracking-widest -mt-1">
        {getVehicleLabel(unit.type)}
      </div>

      {/* Vehicle SVG */}
      <div className="flex justify-center w-full">
        {unit.type === 'buggy' && <ExplorerSVG {...commonProps} />}
        {unit.type === 'engine' && <EngineSVG {...commonProps} />}
        {unit.type === 'tower' && <TowerSVG {...commonProps} />}
        {unit.type === 'rescue' && <RescueSVG {...commonProps} />}
        {unit.type === 'ambulance' && <AmbulanceSVG {...commonProps} />}
        {unit.type === 'medic' && <AmbulanceSVG {...commonProps} />}
        {unit.type === 'brush' && <BrushSVG {...commonProps} />}
      </div>
    </div>
  );
}

/** Filter units to only those shown in the apparatus view (exclude utility, safety) */
export function isApparatusUnit(unit: UnitDef): boolean {
  return unit.type !== 'utility' && unit.type !== 'safety';
}
